import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { MarketingBeforeAfter } from '../components/MarketingBeforeAfter';
import { useAuth } from '../lib/AuthContext';

export const LandingPage: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const { productConfig, balance } = useAuth();
  const isAlphaFree =
    productConfig?.restorationsCurrentlyFree ||
    balance?.restorationsCurrentlyFree ||
    productConfig?.creditMode === 'disabled' ||
    productConfig?.creditMode === 'metered' ||
    productConfig?.stage === 'alpha' ||
    !productConfig;

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mx', `${x}%`);
      el.style.setProperty('--my', `${y}%`);
    };
    el.addEventListener('pointermove', onMove);
    return () => el.removeEventListener('pointermove', onMove);
  }, []);

  const heroFallback = (
    <>
      <p className="kin-hero-aside-label">What stays true</p>
      <ul className="kin-hero-aside-list">
        <li>
          <strong>Restore damage</strong>
          <span>Heal glare, creases, scratches, and fading with restraint.</span>
        </li>
        <li>
          <strong>Preserve identity</strong>
          <span>Keep the same faces — expression, age, and likeness.</span>
        </li>
        <li>
          <strong>Keep the photograph</strong>
          <span>Maintain medium, composition, and historical character.</span>
        </li>
      </ul>
    </>
  );

  return (
    <main>
      <section className="kin-hero" ref={heroRef} aria-label="Kinvera hero">
        <div className="kin-hero-atmosphere" aria-hidden="true" />
        <div className="kin-hero-grain" aria-hidden="true" />
        <div className="kin-hero-layout">
          <div className="kin-hero-panel">
            <div className="kin-hero-brand">
              <BrandLogo to="" size="xl" />
            </div>
            {isAlphaFree && (
              <p className="kin-alpha-pill">Kinvera Alpha · Restorations currently free</p>
            )}
            <h1 className="kin-hero-title">Restore the photo. Preserve the person.</h1>
            <p className="kin-hero-lede">
              Kinvera restores damaged family photographs while protecting facial identity —
              expression, age, skin tone, hairstyle, and the historical character of the original print.
            </p>
            <div className="kin-cta-row">
              <Link to="/login?next=%2Fapp" className="kin-btn kin-btn-primary">
                {isAlphaFree ? 'Try Kinvera during Alpha' : 'Start restoring'}
              </Link>
              <Link to="/how-it-works" className="kin-btn kin-btn-ghost">
                How it works
              </Link>
            </div>
          </div>

          <aside className="kin-hero-aside" aria-label="Kinvera restoration demonstration">
            <MarketingBeforeAfter fallback={heroFallback} />
          </aside>
        </div>
      </section>

      <section className="kin-section kin-section-first">
        <h2>Not generic AI enhancement</h2>
        <p className="kin-section-lede">
          The goal is a carefully restored version of the <em>same</em> family photograph —
          not a replacement face, not smoothed-over identity, not invented detail.
        </p>
        <ul className="kin-promise-grid">
          <li>
            <strong>Facial identity</strong>
            <span>Conservative repair of damaged or obscured faces without swapping likeness.</span>
          </li>
          <li>
            <strong>Expression &amp; age</strong>
            <span>Keep the look, the lines that belong, and the apparent age in the original.</span>
          </li>
          <li>
            <strong>Tone &amp; style</strong>
            <span>Preserve skin tone, hairstyle, clothing, and period photographic character.</span>
          </li>
          <li>
            <strong>Composition</strong>
            <span>Heal glare, creases, and scratches while keeping the frame true to the print.</span>
          </li>
        </ul>
      </section>

      <section className="kin-section kin-section-alt">
        <h2>One careful restoration at a time</h2>
        <p className="kin-section-lede">
          Upload a phone photo of a print. Kinvera inspects the medium, flags damage, then restores
          with museum-minded restraint.
        </p>
        <ol className="kin-steps">
          <li>
            <strong>Inspect</strong>
            <span>Forensic medium and damage analysis before any pixels change.</span>
          </li>
          <li>
            <strong>Approve</strong>
            <span>Confirm subjects, crop glare, paint damage masks if you need them.</span>
          </li>
          <li>
            <strong>Restore</strong>
            <span>
              {isAlphaFree
                ? 'Restorations and refinements are currently free while Kinvera is in Alpha.'
                : 'One credit covers the restore and one included tweak.'}
            </span>
          </li>
        </ol>
        <div className="kin-cta-row" style={{ marginTop: '2rem' }}>
          <Link to="/login?next=%2Fapp" className="kin-btn kin-btn-primary">
            {isAlphaFree ? 'Sign in to restore' : 'Sign in to restore'}
          </Link>
          {!isAlphaFree && (
            <Link to="/pricing" className="kin-btn kin-btn-ghost">
              View pricing
            </Link>
          )}
        </div>
      </section>
    </main>
  );
};
